import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import RichTextEditor from '../text/RichTextEditor';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

export default function EditTextBlockModal({ isOpen, onClose, textBlock, onUpdated }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (textBlock && isOpen) {
      setTitle(textBlock.title || '');
      setContent(textBlock.content || '');
      setIsSaving(false);
    }
  }, [textBlock, isOpen]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const hasChanges = title !== (textBlock?.title || '') || content !== (textBlock?.content || '');

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiHelpers.updateTextBlock(textBlock._id, {
        title: title.trim(),
        content
      });

      if (response.data?.textBlock) {
        onUpdated?.(response.data.textBlock);
        toast.success('Text block updated');
        onClose();
      }
    } catch (error) {
      console.error('Update text block error:', error);
      toast.error(error.response?.data?.error || 'Failed to update text block');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform bg-white dark:bg-surface-900 rounded-2xl shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <PencilSquareIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white">
                      Edit Text Block
                    </Dialog.Title>
                  </div>
                  <button onClick={handleClose} disabled={isSaving} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50">
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Title <span className="text-surface-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={isSaving}
                      maxLength={200}
                      className="w-full px-4 py-2.5 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      placeholder="Give this text block a title..."
                    />
                  </div>

                  {/* Rich Text Editor */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Content
                    </label>
                    <RichTextEditor
                      content={content}
                      onChange={setContent}
                      disabled={isSaving}
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-surface-200 dark:border-surface-700 gap-3">
                  <button
                    onClick={handleClose}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-300 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                    className="btn-primary disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
